import { type ReactNode, useEffect, useRef, useState } from "react";
import {
  useFieldArray,
  Controller,
  type Control,
  type UseFormRegister,
  type UseFormWatch,
  type UseFormSetValue,
  type FieldErrors,
} from "react-hook-form";
import {
  DndContext,
  closestCenter,
  type DragEndEvent,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import { SortableContext, verticalListSortingStrategy, useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { DynamicFieldList } from "@/components/ResearchCms";
import type { TemplateField } from "@/api/researchCms.service";
import type { OrgFormValues } from "../../schemas/orgViewSchemas";
import { calculateOverall, type SarsSlider } from "../../utils/sarsFields";
import { toNullableNumber } from "../../utils/toNullableNumber";
import { PasteNamesDialog } from "./PasteNamesDialog";
import {
  groupSubmissionFields,
  mapSubmissionField,
  type SubmissionFieldGroup,
} from "../../utils/templateFieldMapping";
import { TrashIcon, GripIcon } from "@/components/ResearchCms/icons";
import { SectionVisibilityIcon, PublishableIcon } from "../SectionVisibilityIcon";
import shared from "../cmsShared.module.css";
import styles from "./SubmissionTab.module.css";

type TemplateSubmissionTabProps = {
  control: Control<OrgFormValues>;
  register: UseFormRegister<OrgFormValues>;
  watch: UseFormWatch<OrgFormValues>;
  setValue: UseFormSetValue<OrgFormValues>;
  errors: FieldErrors<OrgFormValues>;
  fields: TemplateField[];
  sliders: SarsSlider[];
  templateType: string | null;
};

// ── Sortable matter row (shared with SubmissionTab) ──

function SortableMatterRow({
  id,
  children,
}: {
  id: string;
  children: (dragHandleProps: {
    listeners: ReturnType<typeof useSortable>["listeners"];
    attributes: ReturnType<typeof useSortable>["attributes"];
  }) => ReactNode;
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id,
  });
  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };
  return (
    <div ref={setNodeRef} style={style} className={isDragging ? styles.dragging : undefined}>
      {children({ listeners, attributes })}
    </div>
  );
}

// ── Field label with optional publishable icon ──

type FieldLabelWithIconProps = {
  label: string;
  publishable: boolean;
  htmlFor?: string;
};

function FieldLabelWithIcon({ label, publishable, htmlFor }: FieldLabelWithIconProps) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 4 }}>
      <label className={shared.sectionSubheading} style={{ margin: 0 }} htmlFor={htmlFor}>
        {label}
      </label>
      {publishable && <PublishableIcon />}
    </div>
  );
}

export function TemplateSubmissionTab({
  control,
  register,
  watch,
  setValue,
  errors,
  fields,
  sliders,
  templateType,
}: TemplateSubmissionTabProps) {
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));
  const [overallSetManually, setOverallSetManually] = useState(false);
  const fieldIdCounterRef = useRef(0);
  function nextFieldId() {
    return `tpl-field-${++fieldIdCounterRef.current}`;
  }

  // ── SARs auto-overall calculation ──

  const overallSlider = sliders.find(slider => slider.scoreKey === "overallSubmission");
  const nonOverallSliders = sliders.filter(slider => slider.scoreKey !== "overallSubmission");
  const nonOverallNames = nonOverallSliders.map(slider => `sarScores.${slider.scoreKey}` as const);
  const nonOverallValues = watch(nonOverallNames) as (number | null)[];
  const nonOverallValuesKey = nonOverallValues
    .map(value => (value === null ? "" : String(value)))
    .join("|");

  const lastComputedOverallRef = useRef<number | null | undefined>(undefined);

  useEffect(() => {
    if (!overallSlider || overallSetManually || sliders.length === 0 || templateType === null) {
      return;
    }

    const currentOverall = watch(`sarScores.${overallSlider.scoreKey}`) as number | null;
    const baseline = lastComputedOverallRef.current;

    if (baseline === undefined) {
      if (currentOverall != null) {
        const componentMax =
          nonOverallSliders.length > 0
            ? Math.max(...nonOverallSliders.map(slider => slider.max))
            : 5;
        lastComputedOverallRef.current = calculateOverall(
          nonOverallValues,
          componentMax,
          overallSlider.max,
        );
        return;
      }
      lastComputedOverallRef.current = currentOverall;
    } else if (currentOverall !== baseline) {
      lastComputedOverallRef.current = currentOverall;
      return;
    }

    const componentMax =
      nonOverallSliders.length > 0 ? Math.max(...nonOverallSliders.map(slider => slider.max)) : 5;
    const computedOverall = calculateOverall(nonOverallValues, componentMax, overallSlider.max);
    setValue(`sarScores.${overallSlider.scoreKey}`, computedOverall, {
      shouldDirty: true,
    });
    lastComputedOverallRef.current = computedOverall;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [overallSlider?.scoreKey, overallSetManually, templateType, nonOverallValuesKey]);

  // ── Field arrays ──

  const summaryMatters = useFieldArray({ control, name: "matterSummaries" });

  // ── String array helpers for DynamicFieldList ──

  const stableIdsRef = useRef<Record<string, string[]>>({});
  const [pasteClientsOpen, setPasteClientsOpen] = useState(false);

  function makeStringFieldProps(name: "practiceHeads" | "otherKeyLawyers" | "keyClients") {
    const values = watch(name);

    if (!stableIdsRef.current[name]) {
      stableIdsRef.current[name] = values.map(() => nextFieldId());
    }
    const ids = stableIdsRef.current[name];
    while (ids.length < values.length) {
      ids.push(nextFieldId());
    }

    return {
      fields: values.map((value, index) => ({
        id: ids[index],
        value,
      })),
      onAdd: () => {
        ids.push(nextFieldId());
        setValue(name, [...values, ""], { shouldDirty: true });
      },
      onRemove: (index: number) => {
        ids.splice(index, 1);
        setValue(
          name,
          values.filter((_, i) => i !== index),
          { shouldDirty: true },
        );
      },
      onMove: (from: number, to: number) => {
        const [movedId] = ids.splice(from, 1);
        ids.splice(to, 0, movedId);
        const updated = [...values];
        const [movedVal] = updated.splice(from, 1);
        updated.splice(to, 0, movedVal);
        setValue(name, updated, { shouldDirty: true });
      },
      onChange: (index: number, value: string) => {
        const updated = [...values];
        updated[index] = value;
        setValue(name, updated, { shouldDirty: true });
      },
    };
  }

  function appendPastedClients(names: string[]) {
    const props = makeStringFieldProps("keyClients");
    const ids = stableIdsRef.current.keyClients;
    for (const _name of names) ids.push(nextFieldId());
    setValue("keyClients", [...props.fields.map(field => field.value), ...names], {
      shouldDirty: true,
    });
    setPasteClientsOpen(false);
  }

  function renderPasteClientsDialog() {
    if (!pasteClientsOpen) return null;
    return (
      <PasteNamesDialog
        title="Paste key clients"
        textBoxLabel="Client names, one per line"
        itemNoun={{ one: "client", other: "clients" }}
        existing={watch("keyClients")}
        onConfirm={appendPastedClients}
        onCancel={() => setPasteClientsOpen(false)}
      />
    );
  }

  const pasteClientsAction = {
    label: "Paste clients",
    onClick: () => setPasteClientsOpen(true),
  };

  // ── Drag-sortable matter list ──

  function renderMatterGroup(
    name: "matterSummaries",
    heading: string,
    addLabel: string,
    emptyMessage: string,
    group: {
      fields: { id: string }[];
      append: (value: { text: string }) => void;
      remove: (index: number) => void;
      move: (from: number, to: number) => void;
    },
  ) {
    const ids = group.fields.map(field => field.id);

    function handleDragEnd(event: DragEndEvent) {
      const { active, over } = event;
      if (over && active.id !== over.id) {
        group.move(ids.indexOf(String(active.id)), ids.indexOf(String(over.id)));
      }
    }

    return (
      <div>
        <h3 className={shared.sectionSubheading}>
          {heading}
          {group.fields.length > 0 && ` (${group.fields.length})`}
        </h3>
        {group.fields.length === 0 && <p className={shared.emptyTableMessage}>{emptyMessage}</p>}
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
          <SortableContext items={ids} strategy={verticalListSortingStrategy}>
            <div className={styles.fieldList}>
              {group.fields.map((field, idx) => (
                <SortableMatterRow key={field.id} id={field.id}>
                  {({ listeners, attributes }) => (
                    <div className={styles.matterRow}>
                      <button
                        type="button"
                        className={styles.dragHandle}
                        {...listeners}
                        {...attributes}
                        aria-label={`Reorder ${heading} ${idx + 1}`}
                      >
                        <GripIcon />
                      </button>
                      <span className={styles.fieldNumber}>{idx + 1}.</span>
                      <textarea
                        rows={1}
                        {...register(`${name}.${idx}.text`)}
                        className={`${shared.textInput} ${styles.matterInputWide}${
                          errors[name]?.[idx]?.text ? ` ${shared.inputError}` : ""
                        }`}
                        placeholder="Matter description"
                      />
                      <button
                        type="button"
                        className={shared.deleteButton}
                        onClick={() => group.remove(idx)}
                        aria-label={`Remove ${heading} ${idx + 1}`}
                      >
                        <TrashIcon />
                      </button>
                    </div>
                  )}
                </SortableMatterRow>
              ))}
            </div>
          </SortableContext>
        </DndContext>
        {group.fields.map(
          (field, idx) =>
            errors[name]?.[idx]?.text && (
              <p key={`error-${field.id}`} className={shared.fieldError}>
                {errors[name]?.[idx]?.text?.message}
              </p>
            ),
        )}
        <button
          type="button"
          className={shared.addButton}
          onClick={() => group.append({ text: "" })}
        >
          {addLabel}
        </button>
      </div>
    );
  }

  // ── Section renderers ──

  function renderRepresentativeMatters(group: SubmissionFieldGroup) {
    const totalMatterCount = summaryMatters.fields.length;
    return (
      <div key={group.section} className={shared.sectionCard} style={{ position: "relative" }}>
        <SectionVisibilityIcon publishable={group.publishable} />
        <h2 className={shared.sectionHeading}>
          Representative Matters
          {totalMatterCount > 0 && ` (${totalMatterCount})`}
        </h2>
        {renderMatterGroup(
          "matterSummaries",
          "Summary matters",
          "+ Add summary matter",
          "No summary matters added yet.",
          summaryMatters,
        )}
      </div>
    );
  }

  function renderKeyPersonnel(group: SubmissionFieldGroup) {
    // Build the personnel sub-sections from whichever fields the template includes
    const personnelFields = group.fields;
    const sections: {
      field: TemplateField;
      formName: "practiceHeads" | "otherKeyLawyers";
    }[] = [];

    for (const field of personnelFields) {
      const mapping = mapSubmissionField(field);
      if (mapping?.formArrayName === "practiceHeads") {
        sections.push({ field, formName: "practiceHeads" });
      } else if (mapping?.formArrayName === "otherKeyLawyers") {
        sections.push({ field, formName: "otherKeyLawyers" });
      }
    }

    return (
      <div key={group.section} className={shared.sectionCard} style={{ position: "relative" }}>
        <SectionVisibilityIcon publishable={group.publishable} />
        <h2 className={shared.sectionHeading}>Key Personnel</h2>
        {sections.map((entry, idx) => {
          const props = makeStringFieldProps(entry.formName);
          return (
            <div key={entry.field.fieldName}>
              {idx > 0 && <hr className={shared.separator} />}
              <FieldLabelWithIcon
                label={`${entry.field.fieldLabel}${props.fields.length > 0 ? ` (${props.fields.length})` : ""}`}
                publishable={entry.field.publishable === 1}
              />
              <DynamicFieldList
                {...props}
                addLabel={`+ Add ${entry.field.fieldLabel.toLowerCase()}`}
                placeholder="Name"
                emptyMessage={`No ${entry.field.fieldLabel.toLowerCase()} added yet.`}
              />
            </div>
          );
        })}
      </div>
    );
  }

  function renderKeyClients(group: SubmissionFieldGroup) {
    const keyClientsProps = makeStringFieldProps("keyClients");
    const clientField = group.fields[0];
    return (
      <div key={group.section} className={shared.sectionCard} style={{ position: "relative" }}>
        <SectionVisibilityIcon publishable={group.publishable} />
        <h2 className={shared.sectionHeading}>Clients</h2>
        <div>
          <FieldLabelWithIcon
            label={`Key Clients${keyClientsProps.fields.length > 0 ? ` (${keyClientsProps.fields.length})` : ""}`}
            publishable={clientField?.publishable === 1}
          />
          <DynamicFieldList
            {...keyClientsProps}
            addLabel="+ Add client"
            secondaryAction={pasteClientsAction}
            placeholder="Client name"
            emptyMessage="No key clients added yet."
          />
        </div>
      </div>
    );
  }

  function renderTeamSize(group: SubmissionFieldGroup) {
    const hasPartners = group.fields.some(
      f => mapSubmissionField(f)?.formPath === "teamSizePartners",
    );
    const hasLawyers = group.fields.some(
      f => mapSubmissionField(f)?.formPath === "teamSizeLawyers",
    );
    const partnersField = group.fields.find(
      f => mapSubmissionField(f)?.formPath === "teamSizePartners",
    );
    const lawyersField = group.fields.find(
      f => mapSubmissionField(f)?.formPath === "teamSizeLawyers",
    );

    return (
      <div key={group.section} className={shared.sectionCard} style={{ position: "relative" }}>
        <SectionVisibilityIcon publishable={group.publishable} />
        <h2 className={shared.sectionHeading}>Team Size</h2>
        <div style={{ display: "flex", gap: 24 }}>
          {hasPartners && (
            <div>
              <FieldLabelWithIcon
                label="Partners"
                publishable={partnersField?.publishable === 1}
                htmlFor="team-size-partners"
              />
              <input
                id="team-size-partners"
                type="number"
                min={0}
                className={shared.textInput}
                {...register("teamSizePartners", {
                  setValueAs: toNullableNumber,
                })}
              />
              {errors.teamSizePartners && (
                <p className={shared.fieldError}>{errors.teamSizePartners.message}</p>
              )}
            </div>
          )}
          {hasLawyers && (
            <div>
              <FieldLabelWithIcon
                label="Lawyers"
                publishable={lawyersField?.publishable === 1}
                htmlFor="team-size-lawyers"
              />
              <input
                id="team-size-lawyers"
                type="number"
                min={0}
                className={shared.textInput}
                {...register("teamSizeLawyers", {
                  setValueAs: toNullableNumber,
                })}
              />
              {errors.teamSizeLawyers && (
                <p className={shared.fieldError}>{errors.teamSizeLawyers.message}</p>
              )}
            </div>
          )}
        </div>
      </div>
    );
  }

  function renderInternalNotes(group: SubmissionFieldGroup) {
    const noteField = group.fields[0];
    return (
      <div key={group.section} className={shared.sectionCard} style={{ position: "relative" }}>
        <SectionVisibilityIcon publishable={group.publishable} />
        <h2 className={shared.sectionHeading}>Internal Notes</h2>
        <FieldLabelWithIcon
          label={noteField?.fieldLabel ?? "Editor Comment Box"}
          publishable={noteField?.publishable === 1}
          htmlFor="editor-comment-box"
        />
        <textarea
          id="editor-comment-box"
          rows={4}
          className={shared.textarea}
          {...register("editorCommentBox")}
          placeholder="Internal notes for editors"
        />
      </div>
    );
  }

  function renderSarScores(group: SubmissionFieldGroup) {
    if (sliders.length === 0 || templateType === null) {
      return null;
    }

    return (
      <div key={group.section} className={shared.sectionCard} style={{ position: "relative" }}>
        <SectionVisibilityIcon publishable={group.publishable} />
        <h2 className={shared.sectionHeading}>SAR Scores</h2>
        <p className={styles.sarDescription}>
          Rate the quality of this firm{"'"}s submission. These scores feed into the SARS reports.
        </p>
        <div className={styles.sarGrid}>
          {sliders.map(slider => (
            <Controller
              key={slider.fieldName}
              control={control}
              name={`sarScores.${slider.scoreKey}`}
              render={({ field }) => {
                const isOverall = slider.scoreKey === "overallSubmission";
                const numericValue = typeof field.value === "number" ? field.value : null;
                const sliderPosition = numericValue ?? 0;
                const pct = (sliderPosition / slider.max) * 100;
                return (
                  <div>
                    <div className={styles.sarLabelRow}>
                      <label className={styles.sarLabel} htmlFor={`sar-${slider.fieldName}`}>
                        {slider.label}
                      </label>
                      <span
                        className={
                          numericValue === null
                            ? `${styles.sarValue} ${styles.sarValueUnscored}`
                            : styles.sarValue
                        }
                        data-sar-value
                      >
                        {numericValue ?? "—"}
                      </span>
                    </div>
                    <div className={styles.sarSliderWrapper}>
                      <input
                        id={`sar-${slider.fieldName}`}
                        type="range"
                        aria-valuetext={numericValue === null ? "Not scored" : undefined}
                        min={0}
                        max={slider.max}
                        step={1}
                        value={sliderPosition}
                        onChange={e => {
                          field.onChange(Number(e.target.value));
                          if (isOverall) {
                            setOverallSetManually(true);
                          }
                        }}
                        className={`${shared.rangeInput} ${styles.sarSlider}`}
                        style={{
                          background: `linear-gradient(to right, var(--color-primary, #2563eb) 0%, var(--color-primary, #2563eb) ${pct}%, var(--color-border, #e2e8f0) ${pct}%, var(--color-border, #e2e8f0) 100%)`,
                        }}
                      />
                    </div>
                    <div className={styles.sarMinMax}>
                      <span>0</span>
                      <span>{slider.max}</span>
                    </div>
                  </div>
                );
              }}
            />
          ))}
        </div>
      </div>
    );
  }

  // ── Template-driven rendering ──

  const groups = groupSubmissionFields(fields);

  // When the template has loaded and produced groups, render them in template order.
  if (groups.length > 0) {
    return (
      <>
        {groups.map(group => {
          switch (group.section) {
            case "representative_matters":
              return renderRepresentativeMatters(group);
            case "key_personnel":
              return renderKeyPersonnel(group);
            case "key_clients":
              return renderKeyClients(group);
            case "team_size":
              return renderTeamSize(group);
            case "internal_notes":
              return renderInternalNotes(group);
            case "sar_scores":
              return renderSarScores(group);
            default:
              return null;
          }
        })}
        {renderPasteClientsDialog()}
      </>
    );
  }

  // ── Fallback: fields not yet loaded, render hardcoded layout ──

  const practiceHeadsProps = makeStringFieldProps("practiceHeads");
  const otherKeyLawyersProps = makeStringFieldProps("otherKeyLawyers");
  const keyClientsProps = makeStringFieldProps("keyClients");
  const totalMatterCount = summaryMatters.fields.length;

  return (
    <>
      {/* Representative Matters */}
      <div className={shared.sectionCard}>
        <h2 className={shared.sectionHeading}>
          Representative Matters
          {totalMatterCount > 0 && ` (${totalMatterCount})`}
        </h2>
        {renderMatterGroup(
          "matterSummaries",
          "Summary matters",
          "+ Add summary matter",
          "No summary matters added yet.",
          summaryMatters,
        )}
      </div>

      {/* Key Personnel */}
      <div className={shared.sectionCard}>
        <h2 className={shared.sectionHeading}>Key Personnel</h2>
        <div>
          <h3 className={shared.sectionSubheading}>
            Practice Heads
            {practiceHeadsProps.fields.length > 0 && ` (${practiceHeadsProps.fields.length})`}
          </h3>
          <DynamicFieldList
            {...practiceHeadsProps}
            addLabel="+ Add practice head"
            placeholder="Name"
            emptyMessage="No practice heads added yet."
          />
        </div>
        <hr className={shared.separator} />
        <div>
          <h3 className={shared.sectionSubheading}>
            Other Key Lawyers
            {otherKeyLawyersProps.fields.length > 0 && ` (${otherKeyLawyersProps.fields.length})`}
          </h3>
          <DynamicFieldList
            {...otherKeyLawyersProps}
            addLabel="+ Add lawyer"
            placeholder="Name"
            emptyMessage="No other key lawyers added yet."
          />
        </div>
      </div>

      {/* Key Clients */}
      <div className={shared.sectionCard}>
        <h2 className={shared.sectionHeading}>Clients</h2>
        <div>
          <h3 className={shared.sectionSubheading}>
            Key Clients
            {keyClientsProps.fields.length > 0 && ` (${keyClientsProps.fields.length})`}
          </h3>
          <DynamicFieldList
            {...keyClientsProps}
            addLabel="+ Add client"
            secondaryAction={pasteClientsAction}
            placeholder="Client name"
            emptyMessage="No key clients added yet."
          />
        </div>
      </div>

      {/* SAR Scores */}
      {sliders.length > 0 && templateType !== null && (
        <div className={shared.sectionCard}>
          <h2 className={shared.sectionHeading}>SAR Scores</h2>
          <p className={styles.sarDescription}>
            Rate the quality of this firm{"'"}s submission. These scores feed into the SARS reports.
          </p>
          <div className={styles.sarGrid}>
            {sliders.map(slider => (
              <Controller
                key={slider.fieldName}
                control={control}
                name={`sarScores.${slider.scoreKey}`}
                render={({ field }) => {
                  const isOverall = slider.scoreKey === "overallSubmission";
                  const numericValue = typeof field.value === "number" ? field.value : null;
                  const sliderPosition = numericValue ?? 0;
                  const pct = (sliderPosition / slider.max) * 100;
                  return (
                    <div>
                      <div className={styles.sarLabelRow}>
                        <label className={styles.sarLabel} htmlFor={`sar-${slider.fieldName}`}>
                          {slider.label}
                        </label>
                        <span
                          className={
                            numericValue === null
                              ? `${styles.sarValue} ${styles.sarValueUnscored}`
                              : styles.sarValue
                          }
                          data-sar-value
                        >
                          {numericValue ?? "—"}
                        </span>
                      </div>
                      <div className={styles.sarSliderWrapper}>
                        <input
                          id={`sar-${slider.fieldName}`}
                          type="range"
                          aria-valuetext={numericValue === null ? "Not scored" : undefined}
                          min={0}
                          max={slider.max}
                          step={1}
                          value={sliderPosition}
                          onChange={e => {
                            field.onChange(Number(e.target.value));
                            if (isOverall) {
                              setOverallSetManually(true);
                            }
                          }}
                          className={`${shared.rangeInput} ${styles.sarSlider}`}
                          style={{
                            background: `linear-gradient(to right, var(--color-primary, #2563eb) 0%, var(--color-primary, #2563eb) ${pct}%, var(--color-border, #e2e8f0) ${pct}%, var(--color-border, #e2e8f0) 100%)`,
                          }}
                        />
                      </div>
                      <div className={styles.sarMinMax}>
                        <span>0</span>
                        <span>{slider.max}</span>
                      </div>
                    </div>
                  );
                }}
              />
            ))}
          </div>
        </div>
      )}
      {renderPasteClientsDialog()}
    </>
  );
}
